/* Marbury Lane birthstone selector.

   Drives the two month selections emitted by snippets/ml-stone-selector.liquid:
   the drawer, which field a pick writes to, and how the values reach the cart.

   ---------------------------------------------------------------------------
   THE TWO PROPERTIES product-form.js READS

   For the bundle path, product-form.js collects every element matching
   [id^='CustomField-<section>-'] and emits, per cart line:

       <input name="items[n][properties][ {fieldName} ]" value="{prevValue}">

   So each field wrapper carries .fieldName and .prevValue, set here and kept in
   sync on every pick. It copies the same value onto every line, which is what
   this product wants: a Buy 2 is a matched pair, two people wearing the same
   two stones, each ring in its own size.

   The plain path needs none of this - the hidden inputs carry
   form="product-form-<id>" so FormData picks them up directly.

   ---------------------------------------------------------------------------
   NO HERO PREVIEW, ON PURPOSE

   An earlier build layered the chosen stones over the hero image the way Indigo
   Lune does. It was cut before launch. Twelve months across two positions is
   144 combinations, and even composited down to 25 layered assets that is 23
   image generations plus a registration pass, spent on an unproven product.
   Olivia Jewelry sells the same category without one.

   If this ring proves out, the layered approach is the way back in: one master
   hero with both stones colourless, plus 12 rectangle and 12 drop recolour
   plates, each clipped in CSS to its own stone region so no alpha channel is
   needed. Generate the plates as constrained edits OF THE MASTER with the
   master attached, never as independent runs - independent runs drift on
   camera, crop and lighting, and a plate out of register makes the ring visibly
   jump the moment a shopper switches month. Do not attempt it as 144 flat
   renders.

   ---------------------------------------------------------------------------
   WHAT THIS HAS TO SURVIVE

   SECTION RE-RENDER. Picking a different ring size makes Shrine's VariantSelects
   fetch the section and swap the HTML in, destroying every field the shopper has
   touched. Values are mirrored into STORE (module scope, outlives the DOM) on
   every pick and restored in connectedCallback. Without this, choosing two
   stones and then changing size would silently clear them. */

(function () {
  'use strict';

  /* Keyed 'section|index'. Module scope on purpose - see note above. */
  var STORE = {};

  class MlStoneSelector extends HTMLElement {
    connectedCallback() {
      this.section = this.dataset.section;
      this.form = document.getElementById(this.dataset.form);
      this.productForm = document.getElementById('ProductForm-' + this.section)
        || this.closest('product-form');

      this.errorEl = this.querySelector('[data-ml-stone-error]');
      this.drawer = this.querySelector('[data-ml-stone-drawer]');
      this.scrim = this.querySelector('[data-ml-stone-scrim]');
      this.fields = Array.prototype.slice.call(this.querySelectorAll('[data-ml-stone-field]'));

      this.openFor = null;
      this.lastFocus = null;

      this.restore();
      this.bind();
    }

    disconnectedCallback() {
      if (this.submitHandler && this.productForm) {
        this.productForm.removeEventListener('submit', this.submitHandler, true);
      }
      document.removeEventListener('keydown', this.keyHandler, true);
      /* The section can be re-rendered with the drawer open. Without this the
         guard and the lock would outlive the element that owns them. */
      document.removeEventListener('wheel', this.scrollGuard);
      document.removeEventListener('touchmove', this.scrollGuard);
      document.documentElement.classList.remove('ml-stone-locked');
    }

    /* ------------------------------------------------------------------ parts */

    field(i) {
      return this.querySelector('[data-ml-stone-field="' + i + '"]');
    }

    input(i) {
      return this.querySelector('[data-ml-stone-input="' + i + '"]');
    }

    /* Builds an asset URL by swapping the filename on one that already exists.

       Liquid cannot hand these over directly: asset_url on a file that has not
       been uploaded is a Theme Check error, and the 24 stone tiles may not be
       uploaded yet. So the snippet passes the URL of this script and the
       filename is replaced here. The version query is dropped, which costs
       cache busting and buys a file that validates before the images land. */
    assetUrl(name) {
      var base = this.dataset.assetBase;
      if (!base) return '';
      return base.split('?')[0].replace(/[^/]+$/, name);
    }

    /* ml-stone-{cut}-{month}.webp, so 24 images: two cuts x twelve months.

       The drawer is ONE grid shared by both fields, but the ring's two stones
       are different cuts. So the tiles reload to the cut being chosen each time
       the drawer opens - pick the drop stone and you see drop-cut gems. That is
       the entire reason for showing images instead of plain colour swatches; a
       shared set would show the wrong shape half the time.

       The disc colour underneath always renders, so a tile whose image fails to
       load drops the image and still reads correctly as that month's colour.
       That is what lets the drawer work before any image is uploaded. */
    paintTiles(cut) {
      var self = this;
      Array.prototype.forEach.call(this.querySelectorAll('[data-ml-stone-pick]'), function (tile) {
        var disc = tile.querySelector('.ml-stone__tile-disc');
        var num = tile.dataset.mlStoneNum;
        if (!disc || !num) return;

        var img = disc.querySelector('img');
        if (!img) {
          img = document.createElement('img');
          img.className = 'ml-stone__tile-img';
          img.alt = '';
          img.loading = 'lazy';
          img.width = 72;
          img.height = 72;
          img.addEventListener('error', function () { img.remove(); });
          disc.appendChild(img);
        }
        img.src = self.assetUrl('ml-stone-' + cut + '-' + num + '.webp');
      });
    }

    /* ------------------------------------------------------------------ wiring */

    bind() {
      var self = this;

      this.addEventListener('click', function (e) {
        var trigger = e.target.closest('[data-ml-stone-trigger]');
        if (trigger) {
          self.open(trigger.dataset.mlStoneTrigger, trigger);
          return;
        }
        var tile = e.target.closest('[data-ml-stone-pick]');
        if (tile) {
          self.pick(tile);
          return;
        }
        if (e.target.closest('[data-ml-stone-close]') || e.target.closest('[data-ml-stone-scrim]')) {
          self.close();
        }
      });

      this.keyHandler = function (e) {
        if (self.openFor === null) return;
        if (e.key === 'Escape') {
          e.preventDefault();
          self.close();
          return;
        }
        if (e.key === 'Tab') self.trapFocus(e);
      };
      document.addEventListener('keydown', this.keyHandler, true);

      /* Holds the page still while the drawer is open, WITHOUT touching layout.

         The obvious approach - overflow:hidden on an ancestor - cannot be used
         above 750px: .product__column-sticky is position:sticky there, and
         changing the scroll container it resolves against snaps the buy-box
         column out of place. Cancelling the scroll event instead moves
         nothing, so nothing can shift. The CSS lock still runs below 750px,
         where there is no sticky column and touch scrolling needs the stronger
         guarantee.

         Attached only while the drawer is open, never permanently: a
         non-passive wheel listener sitting on document defeats the browser's
         scroll optimisation for the whole page. Scrolling inside the drawer is
         let through so the twelve tiles remain reachable. */
      this.scrollGuard = function (e) {
        if (self.drawer && self.drawer.contains(e.target)) return;
        e.preventDefault();
      };

      /* Capture on <product-form> so this runs BEFORE the form's own submit
         listener inside product-form.js. stopPropagation is what actually
         prevents the add-to-cart fetch. */
      if (this.productForm) {
        this.submitHandler = function (e) {
          if (self.validate()) return;
          e.preventDefault();
          e.stopPropagation();
          if (self.errorEl) self.errorEl.hidden = false;
        };
        this.productForm.addEventListener('submit', this.submitHandler, true);
      }
    }

    /* ------------------------------------------------------------------ drawer */

    open(i, trigger) {
      this.openFor = i;
      this.lastFocus = trigger || document.activeElement;

      /* Load the cut being chosen, not the cut that was chosen last time. */
      var field = this.field(i);
      this.paintTiles(field && field.dataset.mlStoneCut ? field.dataset.mlStoneCut : 'drop');

      if (this.scrim) this.scrim.hidden = false;
      if (this.drawer) this.drawer.hidden = false;
      document.documentElement.classList.add('ml-stone-locked');
      document.addEventListener('wheel', this.scrollGuard, { passive: false });
      document.addEventListener('touchmove', this.scrollGuard, { passive: false });
      if (trigger) trigger.setAttribute('aria-expanded', 'true');

      var focusables = this.focusables();
      if (focusables.length) {
        try { focusables[0].focus({ preventScroll: true }); } catch (e) { focusables[0].focus(); }
      }
    }

    close() {
      var trigger = this.openFor !== null
        ? this.querySelector('[data-ml-stone-trigger="' + this.openFor + '"]')
        : null;

      this.openFor = null;
      if (this.scrim) this.scrim.hidden = true;
      if (this.drawer) this.drawer.hidden = true;
      document.documentElement.classList.remove('ml-stone-locked');
      document.removeEventListener('wheel', this.scrollGuard);
      document.removeEventListener('touchmove', this.scrollGuard);
      if (trigger) trigger.setAttribute('aria-expanded', 'false');

      var back = this.lastFocus || trigger;
      if (back) {
        try { back.focus({ preventScroll: true }); } catch (e) { back.focus(); }
      }
    }

    focusables() {
      if (!this.drawer) return [];
      return Array.prototype.slice
        .call(this.drawer.querySelectorAll('button:enabled, [href], [tabindex]:not([tabindex^="-"])'))
        .filter(function (el) { return el.offsetParent !== null; });
    }

    trapFocus(e) {
      var list = this.focusables();
      if (!list.length) return;
      var first = list[0];
      var last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    /* ---------------------------------------------------------------- selection */

    pick(tile) {
      if (this.openFor === null) return;
      var i = this.openFor;
      this.set(i, tile.dataset.mlStonePick, tile.dataset.mlStoneColour, tile.dataset.mlStoneHex);
      this.close();
    }

    set(i, month, colour, hex) {
      var input = this.input(i);
      if (!input) return;

      input.value = month;
      STORE[this.section + '|' + i] = { month: month, colour: colour, hex: hex };

      this.paint(i, month, colour, hex);
      this.sync(i);
      this.clearError();
    }

    paint(i, month, colour, hex) {
      var field = this.field(i);
      if (!field) return;

      var value = field.querySelector('[data-ml-stone-value]');
      if (value) value.textContent = month + ' · ' + colour;

      var swatch = field.querySelector('[data-ml-stone-swatch]');
      if (swatch) {
        swatch.hidden = false;
        swatch.style.setProperty('--stone', hex);
      }
      field.classList.add('ml-stone__field--set');
    }

    /* Mirrors one selection onto the wrapper's .fieldName and .prevValue, which
       is what product-form.js reads at submit time. */
    sync(i) {
      var field = this.field(i);
      var input = this.input(i);
      if (!field || !input) return;
      field.fieldName = input.dataset.mlStoneProp;
      field.prevValue = input.value;
    }

    restore() {
      var self = this;
      [1, 2].forEach(function (i) {
        var saved = STORE[self.section + '|' + i];
        if (saved && saved.month) {
          var input = self.input(i);
          if (input) input.value = saved.month;
          self.paint(i, saved.month, saved.colour, saved.hex);
        }
        self.sync(i);
      });
    }

    /* --------------------------------------------------------------- feedback */

    clearError() {
      var self = this;
      var missing = [1, 2].some(function (i) {
        var input = self.input(i);
        return !input || !input.value;
      });
      if (!missing) {
        if (this.errorEl) this.errorEl.hidden = true;
        this.fields.forEach(function (f) { f.classList.remove('ml-stone__field--error'); });
      }
    }

    validate() {
      var self = this;
      var missing = [];
      [1, 2].forEach(function (i) {
        var input = self.input(i);
        var field = self.field(i);
        if (!input || !input.value) {
          missing.push(i);
          if (field) field.classList.add('ml-stone__field--error');
        } else if (field) {
          field.classList.remove('ml-stone__field--error');
        }
      });

      if (missing.length) {
        var trigger = this.querySelector('[data-ml-stone-trigger="' + missing[0] + '"]');
        if (trigger) {
          try { trigger.focus({ preventScroll: false }); } catch (e) { trigger.focus(); }
        }
      }
      return missing.length === 0;
    }
  }

  if (!customElements.get('ml-stone-selector')) {
    customElements.define('ml-stone-selector', MlStoneSelector);
  }
})();
